import React, { useEffect, useState } from 'react';
import { Download, Smartphone, Share, PlusSquare, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { usePWAInstall } from '@/hooks/usePWAInstall';

/**
 * EntregadorInstallGate
 * Modal mostrado sempre que o entregador abre o app sem ter instalado o PWA.
 * - Não persiste "dismiss" entre sessões (o objetivo é nudgear instalação).
 * - Permite "Continuar mesmo assim" para não bloquear o uso emergencial.
 * - Detecta Android Chrome (prompt nativo) e iOS Safari (instruções manuais).
 */
const EntregadorInstallGate = () => {
  const { isInstallable, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [open, setOpen] = useState(false);
  const [iosStep, setIosStep] = useState(false);

  useEffect(() => {
    // Abre uma única vez por sessão (não em cada navegação interna)
    if (isInstalled) return;
    const shownThisSession = sessionStorage.getItem('rota017_install_shown') === 'true';
    if (shownThisSession) return;
    // Aguarda 1s pra não atropelar o load
    const t = setTimeout(() => {
      // Só mostra se for instalável OU iOS (caso contrário não há o que oferecer)
      if (isInstallable || isIOS) {
        setOpen(true);
        sessionStorage.setItem('rota017_install_shown', 'true');
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [isInstallable, isInstalled, isIOS]);

  const handleInstall = async () => {
    if (isIOS) {
      setIosStep(true);
      return;
    }
    const result = await promptInstall();
    if (result.outcome === 'accepted') {
      setOpen(false);
    }
  };

  const handleContinuar = () => setOpen(false);

  if (isInstalled) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-0"
        data-testid="entregador-install-gate"
      >
        {!iosStep ? (
          <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 text-white p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div className="bg-white/20 backdrop-blur p-3 rounded-2xl">
                <Smartphone className="h-8 w-8" />
              </div>
              <button
                onClick={handleContinuar}
                className="text-white/70 hover:text-white"
                aria-label="Fechar"
                data-testid="install-gate-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-orange-100 mb-2">
                <Sparkles className="h-3.5 w-3.5" />
                Recomendado para entregadores
              </div>
              <h2 className="text-2xl font-bold leading-tight">
                Instale o Rota 017 no seu celular
              </h2>
              <p className="mt-2 text-orange-50 text-sm leading-relaxed">
                Receba notificações de novas entregas mesmo com o app fechado, abra
                direto da tela inicial e mantenha o pedido aberto mesmo trocando
                de app.
              </p>
            </div>

            <ul className="text-sm text-orange-50 space-y-2">
              <li className="flex gap-2">
                <span className="text-white">✓</span>
                <span>Notificações em tempo real de pedidos disponíveis</span>
              </li>
              <li className="flex gap-2">
                <span className="text-white">✓</span>
                <span>Abre direto na sua última tela</span>
              </li>
              <li className="flex gap-2">
                <span className="text-white">✓</span>
                <span>Funciona offline para visualizar entregas em andamento</span>
              </li>
            </ul>

            <div className="space-y-2 pt-2">
              <Button
                onClick={handleInstall}
                className="w-full bg-white text-orange-700 hover:bg-orange-50 font-bold h-12 text-base"
                data-testid="install-gate-install-btn"
              >
                <Download className="h-5 w-5 mr-2" />
                {isIOS ? 'Ver como instalar' : 'Instalar agora'}
              </Button>
              <button
                onClick={handleContinuar}
                className="w-full text-orange-100 hover:text-white text-sm py-2"
                data-testid="install-gate-continue-btn"
              >
                Continuar pelo navegador
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white p-6 space-y-5">
            <div className="flex items-center gap-2 text-orange-600">
              <Smartphone className="h-6 w-6" />
              <h2 className="text-lg font-bold">Instalar no iPhone</h2>
            </div>
            <p className="text-sm text-slate-600">
              No Safari, siga os 3 passos abaixo:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl">
                <span className="bg-orange-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold flex-shrink-0">
                  1
                </span>
                <div className="text-sm">
                  Toque no botão <Share className="h-4 w-4 inline text-blue-500" />{' '}
                  <strong>Compartilhar</strong> na barra inferior.
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl">
                <span className="bg-orange-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold flex-shrink-0">
                  2
                </span>
                <div className="text-sm">
                  Role e toque em <PlusSquare className="h-4 w-4 inline" />{' '}
                  <strong>Adicionar à Tela de Início</strong>.
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl">
                <span className="bg-orange-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold flex-shrink-0">
                  3
                </span>
                <div className="text-sm">
                  Toque em <strong>Adicionar</strong>. O Rota 017 aparece como app
                  na sua tela inicial.
                </div>
              </div>
            </div>
            <Button
              onClick={() => setOpen(false)}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="install-gate-ios-done"
            >
              Entendi
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EntregadorInstallGate;
